import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { Order, OrderStatus, PaymentStatus } from "@shared/types";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { AdminLayout } from "./AdminLayout";

const STATUSES: OrderStatus[] = ["pending", "confirmed", "shipped", "delivered", "cancelled"];
const PAYMENT_STATUSES: PaymentStatus[] = ["unpaid", "deposit_paid", "paid", "skipped"];

export function AdminOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (id) api.admin.orders.get(id).then(setOrder);
  };
  useEffect(load, [id]);

  const update = async (payload: { status?: OrderStatus; paymentStatus?: PaymentStatus }) => {
    if (!id) return;
    setSaving(true);
    try {
      const updated = await api.admin.orders.update(id, payload);
      setOrder(updated);
    } finally {
      setSaving(false);
    }
  };

  if (!order) {
    return (
      <AdminLayout>
        <p className="text-sm text-stone-500">Loading…</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-xl font-medium text-ink">{order.orderNumber}</h1>
        <span className="text-xs text-stone-500">{new Date(order.createdAt).toLocaleString()}</span>
      </div>

      <div className="grid gap-6 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <div className="rounded-xl border border-stone-300 bg-white p-5">
            <h2 className="mb-3 font-display text-base font-medium text-ink">Items</h2>
            <div className="flex flex-col divide-y divide-stone-100">
              {order.items.map((item) => (
                <div key={item.id} className="flex items-center justify-between py-2 text-sm">
                  <div className="flex items-center gap-3">
                    {item.imageUrl && <img src={item.imageUrl} alt="" className="h-10 w-10 rounded object-cover" />}
                    <span className="text-ink">
                      {item.qty} × {item.productName}
                    </span>
                  </div>
                  <span className="text-stone-700">{formatMoney(item.unitPriceCents * item.qty, order.currency)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-stone-300 pt-3 text-sm font-semibold">
              <span className="text-ink">Subtotal</span>
              <span className="text-ink">{formatMoney(order.subtotalCents, order.currency)}</span>
            </div>
            {order.requiresDeposit && (
              <div className="flex items-center justify-between pt-1 text-sm">
                <span className="text-stone-700">Deposit required</span>
                <span className="text-stone-700">{formatMoney(order.depositAmountCents, order.currency)}</span>
              </div>
            )}
          </div>

          <div className="mt-6 rounded-xl border border-stone-300 bg-white p-5">
            <h2 className="mb-3 font-display text-base font-medium text-ink">Customer & delivery</h2>
            <dl className="grid grid-cols-[100px_1fr] gap-y-2 text-sm">
              <dt className="text-stone-500">Name</dt>
              <dd className="text-ink">{order.customerName}</dd>
              <dt className="text-stone-500">Phone</dt>
              <dd className="text-ink">{order.customerPhone}</dd>
              {order.customerEmail && (
                <>
                  <dt className="text-stone-500">Email</dt>
                  <dd className="text-ink">{order.customerEmail}</dd>
                </>
              )}
              <dt className="text-stone-500">Address</dt>
              <dd className="text-ink">
                {order.shippingAddress || order.city ? (
                  <>
                    {order.shippingAddress}
                    {order.city ? `, ${order.city}` : ""}
                  </>
                ) : (
                  <span className="text-stone-500">Call the customer to confirm delivery</span>
                )}
              </dd>
              {order.notes && (
                <>
                  <dt className="text-stone-500">Notes</dt>
                  <dd className="text-ink">{order.notes}</dd>
                </>
              )}
            </dl>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className="rounded-xl border border-stone-300 bg-white p-5">
            <h2 className="mb-3 font-display text-base font-medium text-ink">Order status</h2>
            <select
              disabled={saving}
              value={order.status}
              onChange={(e) => update({ status: e.target.value as OrderStatus })}
              className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm capitalize text-ink"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="rounded-xl border border-stone-300 bg-white p-5">
            <h2 className="mb-1 font-display text-base font-medium text-ink">Payment status</h2>
            <p className="mb-3 text-xs text-stone-500">
              Method: <span className="capitalize">{order.paymentMethod}</span>. Mark paid manually once you've
              confirmed cash, bank transfer, or M-Pesa outside the site.
            </p>
            <select
              disabled={saving}
              value={order.paymentStatus}
              onChange={(e) => update({ paymentStatus: e.target.value as PaymentStatus })}
              className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm capitalize text-ink"
            >
              {PAYMENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replace("_", " ")}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
